import React, { useState } from 'react';
import configDetails from '../Config/Config';

const FeedbackModal = ({ isOpen, onClose, onSubmit, showNotification, authIdToken }) => {
  const [feedbackText, setFeedbackText] = useState('');
  const [selectedRating, setSelectedRating] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showValidationError, setShowValidationError] = useState(false);

  const emojiRatings = [
    { emoji: '😞', label: 'Bad', value: 'Bad' },
    { emoji: '😐', label: 'Neutral', value: 'Neutral' },
    { emoji: '🙂', label: 'Good', value: 'Good' },
    { emoji: '😃', label: 'Excellent', value: 'Excellent' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Both rating and comment are required
    if (!selectedRating || !feedbackText.trim()) {
      setShowValidationError(true);
      return;
    }
    
    setShowValidationError(false);

    setIsSubmitting(true);
    
    try {
      const { trackingUrl, FEEDBACK } = configDetails;
      const payload = {
        feedback_source: 'web',
        feedback_text: feedbackText.trim() || undefined,
        feedback_rating: selectedRating || undefined
      };

      const response = await fetch(`${trackingUrl}${FEEDBACK}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authIdToken
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        showNotification('Thank you for your feedback!', 'success');
        handleClose();
        if (onSubmit) onSubmit();
      } else {
        throw new Error('Failed to submit feedback');
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      showNotification('Failed to submit feedback. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFeedbackText('');
    setSelectedRating('');
    setIsSubmitting(false);
    setShowValidationError(false);
    onClose();
  };

  const handleRatingSelect = (rating) => {
    setSelectedRating(selectedRating === rating.value ? '' : rating.value);
  };

  if (!isOpen) return null;

  return (
    <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog">
      <div className="modal-dialog modal-dialog-centered" role="document">
        <div className="modal-content">
          <div className="modal-header border-0 pb-0">
            <h5 className="modal-title fw-bold text-primary">Share Your Feedback</h5>
            <button
              type="button"
              className="btn-close"
              onClick={handleClose}
              disabled={isSubmitting}
            ></button>
          </div>
          
          <form onSubmit={handleSubmit}>
            <div className="modal-body pt-2">
              <p className="text-muted mb-4">
                Help us improve by sharing your thoughts. Your feedback is anonymous.
              </p>
              
              {/* Emoji Rating Section */}
              <div className="mb-4">
                <label className={`form-label fw-medium mb-3 ${showValidationError && !selectedRating ? 'text-danger' : ''}`}>
                  How was your experience? <span className="text-danger">*</span>
                </label>
                {showValidationError && !selectedRating && (
                  <div className="text-danger small mb-2">Please select a rating.</div>
                )}
                <div className="d-flex justify-content-center gap-3">
                  {emojiRatings.map((rating) => (
                    <button
                      key={rating.value}
                      type="button"
                      className={`btn btn-outline-light border-2 p-3 d-flex flex-column align-items-center gap-1 ${
                        selectedRating === rating.value 
                          ? 'border-primary bg-light' 
                          : 'border-light-subtle'
                      }`}
                      onClick={() => handleRatingSelect(rating)}
                      disabled={isSubmitting}
                      style={{ 
                        minWidth: '70px',
                        transition: 'all 0.2s ease',
                        transform: selectedRating === rating.value ? 'scale(1.05)' : 'scale(1)'
                      }}
                    >
                      <span style={{ fontSize: '24px' }}>{rating.emoji}</span>
                      <small className={`fw-medium ${
                        selectedRating === rating.value ? 'text-primary' : 'text-muted'
                      }`}>
                        {rating.label}
                      </small>
                    </button>
                  ))}
                </div>
              </div>

              {/* Text Feedback Section */}
              <div className="mb-3">
                <label htmlFor="feedbackText" className={`form-label fw-medium ${showValidationError && !feedbackText.trim() ? 'text-danger' : ''}`}>
                  Comments <span className="text-danger">*</span>
                </label>
                {showValidationError && !feedbackText.trim() && (
                  <div className="text-danger small mb-2">Please enter your comments.</div>
                )}
                <textarea
                  id="feedbackText"
                  className={`form-control ${showValidationError && !feedbackText.trim() ? 'border-danger' : 'border-light-subtle'}`}
                  rows="4"
                  placeholder="Tell us what you think..."
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                  disabled={isSubmitting}
                  style={{ resize: 'none' }}
                ></textarea>
              </div>
            </div>

            <div className="modal-footer border-0 pt-0">
              <button
                type="button"
                className="btn btn-outline-secondary me-2"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary px-4"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span 
                      className="spinner-border spinner-border-sm me-2" 
                      role="status"
                    ></span>
                    Submitting...
                  </>
                ) : (
                  'Submit Feedback'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FeedbackModal;
